"use client";

import { useEffect, useState } from "react";
import { auth, db } from "@/config/firebase";
import { doc, setDoc } from "firebase/firestore";

export interface UserPreferences {
  theme: "sanctum" | "ethica" | "immanentia" | "abyssum";
  font: "clasica" | "ejecutiva";
  layout: "zen" | "compacto";
  bionic: boolean;
}

const STORAGE_KEY = "copleston_prefs";
const DEFAULT_PREFS: UserPreferences = {
  theme: "sanctum",
  font: "clasica",
  layout: "zen",
  bionic: false,
};

export const useSettings = () => {
  const [preferences, setPreferences] = useState<UserPreferences>(DEFAULT_PREFS);

  // Helper to load settings from localStorage and document
  const loadPreferences = () => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (saved) {
        const parsed = JSON.parse(saved);
        const validated: UserPreferences = {
          theme: ["sanctum", "ethica", "immanentia", "abyssum"].includes(parsed.theme)
            ? parsed.theme
            : DEFAULT_PREFS.theme,
          font: ["clasica", "ejecutiva"].includes(parsed.font)
            ? parsed.font
            : DEFAULT_PREFS.font,
          layout: ["zen", "compacto"].includes(parsed.layout)
            ? parsed.layout
            : DEFAULT_PREFS.layout,
          bionic: typeof parsed.bionic === "boolean" ? parsed.bionic : DEFAULT_PREFS.bionic,
        };
        setPreferences(validated);
        return validated;
      }
    } catch (e) {
      console.error("Error reading localStorage:", e);
    }
    return DEFAULT_PREFS;
  };

  // Sync settings when component mounts
  useEffect(() => {
    const initialPrefs = loadPreferences();
    applyDOM(initialPrefs);

    // Listen to changes triggered by other components or auth sync
    const handleChanged = () => {
      const freshPrefs = loadPreferences();
      applyDOM(freshPrefs);
    };
    window.addEventListener("copleston_settings_changed", handleChanged);
    return () => window.removeEventListener("copleston_settings_changed", handleChanged);
  }, []);

  const applyDOM = (prefs: UserPreferences) => {
    if (typeof document === "undefined") return;
    const root = document.documentElement;
    root.setAttribute("data-theme", prefs.theme);
    root.setAttribute("data-font", prefs.font);
    root.setAttribute("data-layout", prefs.layout);
    if (prefs.bionic) {
      root.classList.add("bionic-mode");
    } else {
      root.classList.remove("bionic-mode");
    }
  };

  const savePreferences = async (newPrefs: UserPreferences) => {
    setPreferences(newPrefs);
    applyDOM(newPrefs);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(newPrefs));

    // Sincronizar con Firestore si el usuario está autenticado
    const currentUser = auth.currentUser;
    if (currentUser) {
      try {
        const userDocRef = doc(db, "usuarios", currentUser.uid);
        await setDoc(userDocRef, { preferencias_diseno: newPrefs }, { merge: true });
      } catch (e) {
        console.warn("Firestore preferences save error:", e);
      }
    }

    // Broadcast change
    window.dispatchEvent(new Event("copleston_settings_changed"));
  };

  const setTheme = (theme: UserPreferences["theme"]) => {
    savePreferences({ ...preferences, theme });
  };

  const setFont = (font: UserPreferences["font"]) => {
    savePreferences({ ...preferences, font });
  };

  const setLayout = (layout: UserPreferences["layout"]) => {
    savePreferences({ ...preferences, layout });
  };

  const setBionic = (bionic: boolean) => {
    savePreferences({ ...preferences, bionic });
  };

  return {
    preferences,
    setTheme,
    setFont,
    setLayout,
    setBionic,
  };
};
